import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';

interface ThreePLOrdersProps {
  token: string;
  onLogout: () => void;
}

interface VeeqoOrder {
  id: number;
  number: string;
  status: string;
  createdAt: string;
  channel: string;
  customer: {
    name: string;
    email: string;
    phone: string;
  };
  deliverTo: {
    name: string;
    businessName: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phone: string;
    email: string;
  };
  lineItems: VeeqoLineItem[];
  totalValue: number;
  currency: string;
  shipments?: {
    trackingNumber: string;
    carrier: string;
    shippedAt: string;
    trackingUrl: string;
  }[];
}

interface VeeqoLineItem {
  id: number;
  sellableId: number;
  sku: string;
  asin: string;
  title: string;
  variantTitle: string;
  quantity: number;
  pricePerUnit: number;
  imageUrl: string;
  weight: number;
  weightUnit: string;
  savedData?: {
    country_of_origin: string;
    hts_code: string;
    hts_description: string;
    cogs_usd: number;
    manufacturing_address: any;
    is_food: boolean;
    is_cosmetic_product: boolean;
    fda_product_code: string;
  };
  // Editable fields (local state)
  countryOfOrigin?: string;
  htsCode?: string;
  htsDescription?: string;
  declaredValueUSD?: number;
  manufacturingAddress?: any;
  isFood?: boolean;
  isCosmeticProduct?: boolean;
  fdaProductCode?: string;
}

const COUNTRY_OPTIONS = [
  { code: 'AD', name: 'Andorra' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AI', name: 'Anguilla' },
  { code: 'AL', name: 'Albania' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AO', name: 'Angola' },
  { code: 'AQ', name: 'Antarctica' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AT', name: 'Austria' },
  { code: 'AU', name: 'Australia' },
  { code: 'AW', name: 'Aruba' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BI', name: 'Burundi' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BM', name: 'Bermuda' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BZ', name: 'Belize' },
  { code: 'CA', name: 'Canada' },
  { code: 'CD', name: 'Congo (Democratic Republic)' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'CG', name: 'Congo (Republic)' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'CI', name: 'Ivory Coast' },
  { code: 'CL', name: 'Chile' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CV', name: 'Cape Verde' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DE', name: 'Germany' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EE', name: 'Estonia' },
  { code: 'EG', name: 'Egypt' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'ES', name: 'Spain' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GE', name: 'Georgia' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'GR', name: 'Greece' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HR', name: 'Croatia' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HU', name: 'Hungary' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IN', name: 'India' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IR', name: 'Iran' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JO', name: 'Jordan' },
  { code: 'JP', name: 'Japan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'KP', name: 'North Korea' },
  { code: 'KR', name: 'South Korea' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LY', name: 'Libya' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MD', name: 'Moldova' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'ML', name: 'Mali' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'MO', name: 'Macao' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MT', name: 'Malta' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MV', name: 'Maldives' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MX', name: 'Mexico' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NO', name: 'Norway' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'OM', name: 'Oman' },
  { code: 'PA', name: 'Panama' },
  { code: 'PE', name: 'Peru' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PL', name: 'Poland' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' },
  { code: 'RS', name: 'Serbia' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SN', name: 'Senegal' },
  { code: 'SO', name: 'Somalia' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'SY', name: 'Syria' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'TD', name: 'Chad' },
  { code: 'TG', name: 'Togo' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'UG', name: 'Uganda' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
];

const ThreePLOrders: React.FC<ThreePLOrdersProps> = ({ token, onLogout }) => {
  const navigate = useNavigate();

  // View state
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedOrder, setSelectedOrder] = useState<VeeqoOrder | null>(null);

  // Order list state
  const [orders, setOrders] = useState<VeeqoOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('awaiting_fulfillment');

  // Detail view state
  const [items, setItems] = useState<VeeqoLineItem[]>([]);
  const [processingItem, setProcessingItem] = useState<string | null>(null);

  // Bulk action state
  const [classifyingAll, setClassifyingAll] = useState(false);
  const [lookingUpAll, setLookingUpAll] = useState(false);
  const [uploadingCatalogue, setUploadingCatalogue] = useState(false);
  const [submittingSSB, setSubmittingSSB] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, label: '' });

  // SSB rate check state (per-item by index)
  const [ssbVerifiedData, setSsbVerifiedData] = useState<Record<number, { dutyAmount: number; dutyRate: string; verified: boolean }>>({});
  const [ssbRateLoading, setSsbRateLoading] = useState<Record<number, boolean>>({});

  // Password prompt
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [passwordAction, setPasswordAction] = useState<'catalogue' | 'ssb' | null>(null);
  const [password, setPassword] = useState('');

  // SSB submissions tracking (keyed by Veeqo order ID)
  const [ssbSubmissions, setSSBSubmissions] = useState<Record<number, { ssb_uuid: string; ssb_tracking_code: string | null; ssb_carrier: string | null; synced_to_veeqo: boolean; synced_at: string | null; tracking_numbers?: string[]; status_name?: string }>>({});
  // SSB shipments from API (keyed by customer_order / order number) — for matching legacy orders
  const [ssbShipmentsMap, setSSBShipmentsMap] = useState<Record<string, any>>({});
  const [syncingOrderId, setSyncingOrderId] = useState<number | null>(null);

  // Load orders + SSB submissions together so we can cross-reference
  const loadOrders = useCallback(async () => {
    try {
      setLoadingOrders(true);
      setError(null);
      const [ordersResult, ssbResult] = await Promise.all([
        apiService.get3PLOrders(page, statusFilter),
        apiService.getSSBSubmissions()
      ]);

      if (ordersResult.success && ordersResult.data) {
        const fetchedOrders = ordersResult.data.orders || [];
        setOrders(fetchedOrders);
        setPagination(ordersResult.data.pagination || null);

        // Build SSB submissions map from DB records (keyed by order ID)
        const dbMap: Record<number, any> = {};
        if (ssbResult.success && ssbResult.data) {
          for (const sub of ssbResult.data) {
            dbMap[sub.veeqo_order_id] = sub;
          }
        }

        // Store SSB shipments map from API (keyed by order number)
        const apiShipments = (ssbResult.success && ssbResult.ssbShipments) ? ssbResult.ssbShipments : {};
        setSSBShipmentsMap(apiShipments);

        // Merge: for each order, check DB first, then fall back to SSB API match by order number
        const merged: Record<number, any> = { ...dbMap };
        for (const order of fetchedOrders) {
          if (!merged[order.id] && apiShipments[order.number]) {
            const ssb = apiShipments[order.number];
            merged[order.id] = {
              veeqo_order_id: order.id,
              veeqo_order_number: order.number,
              ssb_uuid: ssb.ssb_uuid,
              ssb_tracking_code: ssb.tracking_numbers?.[0] || null,
              ssb_carrier: ssb.carrier || null,
              synced_to_veeqo: false,
              synced_at: null,
              tracking_numbers: ssb.tracking_numbers || [],
              status_name: ssb.status_name || '',
              // Flag this as auto-matched from SSB API (not yet in DB)
              _fromSSBAPI: true
            };
          }
        }
        setSSBSubmissions(merged);
      } else {
        setError(ordersResult.error || 'Failed to load orders');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoadingOrders(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Open order detail - fetches fresh saved data for each ASIN
  const openOrder = async (order: VeeqoOrder) => {
    setSelectedOrder(order);
    setSsbVerifiedData({});
    setSsbRateLoading({});

    // Start with basic items from the order
    const baseItems = order.lineItems.map(item => ({
      ...item,
      countryOfOrigin: item.savedData?.country_of_origin || '',
      htsCode: item.savedData?.hts_code || '',
      htsDescription: item.savedData?.hts_description || '',
      declaredValueUSD: item.savedData?.cogs_usd || item.pricePerUnit || 0,
      manufacturingAddress: item.savedData?.manufacturing_address || null,
      isFood: item.savedData?.is_food || false,
      isCosmeticProduct: item.savedData?.is_cosmetic_product || false,
      fdaProductCode: item.savedData?.fda_product_code || '',
    }));
    setItems(baseItems);
    setView('detail');

    // Now fetch fresh saved data for each unique ASIN
    const uniqueAsins = [...new Set(baseItems.map(item => item.asin).filter(Boolean))];
    if (uniqueAsins.length === 0) return;

    const freshData: Record<string, any> = {};
    await Promise.all(
      uniqueAsins.map(async (asin) => {
        try {
          const result = await apiService.get3PLASINData(asin);
          if (result.success && result.data) {
            freshData[asin] = result.data;
          }
        } catch (err) {
          // Non-fatal
        }
      })
    );

    if (Object.keys(freshData).length === 0) return;

    // Merge fresh data into items
    setItems(prev => prev.map(item => {
      const saved = freshData[item.asin];
      if (!saved) return item;
      return {
        ...item,
        countryOfOrigin: saved.country_of_origin || item.countryOfOrigin || '',
        htsCode: saved.hts_code || item.htsCode || '',
        htsDescription: saved.hts_description || item.htsDescription || '',
        declaredValueUSD: saved.cogs_usd || item.declaredValueUSD || item.pricePerUnit || 0,
        manufacturingAddress: saved.manufacturing_address || item.manufacturingAddress || null,
        isFood: saved.is_food || false,
        isCosmeticProduct: saved.is_cosmetic_product || false,
        fdaProductCode: saved.fda_product_code || '',
        savedData: {
          country_of_origin: saved.country_of_origin || '',
          hts_code: saved.hts_code || '',
          hts_description: saved.hts_description || '',
          cogs_usd: saved.cogs_usd || 0,
          manufacturing_address: saved.manufacturing_address || null,
          is_food: saved.is_food || false,
          is_cosmetic_product: saved.is_cosmetic_product || false,
          fda_product_code: saved.fda_product_code || '',
        }
      };
    }));
  };

  // Back to list
  const backToList = () => {
    setView('list');
    setSelectedOrder(null);
    setItems([]);
  };

  // Update a single item field and save to DB
  const updateItem = async (index: number, field: string, value: any) => {
    const newItems = [...items];
    (newItems[index] as any)[field] = value;
    setItems(newItems);

    // Save to 3PL ASIN data
    const item = newItems[index];
    const asin = item.asin || item.sku;
    if (!asin) return;

    const saveData: any = {};
    if (field === 'countryOfOrigin') saveData.country_of_origin = value;
    if (field === 'htsCode') saveData.hts_code = value;
    if (field === 'htsDescription') saveData.hts_description = value;
    if (field === 'declaredValueUSD') saveData.cogs_usd = value;
    if (field === 'manufacturingAddress') saveData.manufacturing_address = value;
    if (field === 'isFood') saveData.is_food = value ? 1 : 0;
    if (field === 'isCosmeticProduct') saveData.is_cosmetic_product = value ? 1 : 0;
    if (field === 'fdaProductCode') saveData.fda_product_code = value;

    try {
      await apiService.save3PLASINData(asin, saveData);
    } catch (err) {
      console.error('Failed to save ASIN data:', err);
    }
  };

  // Update ASIN text in local state (no API call)
  const updateItemASINText = (index: number, newAsin: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], asin: newAsin };
    setItems(newItems);
  };

  // Reload saved data when ASIN field loses focus
  const reloadASINData = async (index: number) => {
    const item = items[index];
    const asin = item.asin;
    if (!asin || asin.length < 5) return;

    try {
      const result = await apiService.get3PLASINData(asin);
      if (result.success && result.data) {
        const saved = result.data;
        const newItems = [...items];
        newItems[index] = {
          ...newItems[index],
          countryOfOrigin: saved.country_of_origin || newItems[index].countryOfOrigin || '',
          htsCode: saved.hts_code || newItems[index].htsCode || '',
          htsDescription: saved.hts_description || newItems[index].htsDescription || '',
          declaredValueUSD: saved.cogs_usd || newItems[index].declaredValueUSD || 0,
          manufacturingAddress: saved.manufacturing_address || newItems[index].manufacturingAddress || null,
          isFood: saved.is_food || false,
          isCosmeticProduct: saved.is_cosmetic_product || false,
          fdaProductCode: saved.fda_product_code || '',
        };
        newItems[index].savedData = {
          ...newItems[index].savedData,
          country_of_origin: saved.country_of_origin || '',
          hts_code: saved.hts_code || '',
          hts_description: saved.hts_description || '',
          cogs_usd: saved.cogs_usd || 0,
          manufacturing_address: saved.manufacturing_address || null,
          is_food: saved.is_food || false,
          is_cosmetic_product: saved.is_cosmetic_product || false,
          fda_product_code: saved.fda_product_code || '',
        };
        setItems(newItems);
        // Clear SSB verified data for this item since data may have changed
        setSsbVerifiedData(prev => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to load ASIN data:', err);
    }
  };

  // Classify HTS for a single item
  const classifyHTS = async (index: number) => {
    const item = items[index];
    const itemKey = `hts-${index}`;
    setProcessingItem(itemKey);

    try {
      const result = await apiService.classify3PLHTS(
        item.title,
        item.countryOfOrigin,
        item.asin
      );

      if (result.success && result.htsCodes && result.htsCodes.length > 0) {
        const topMatch = result.htsCodes[0];
        const htsCode = topMatch.htsCode || topMatch.TariffNum || '';
        const htsDesc = topMatch.description || topMatch.CommodityDesc || '';

        await updateItem(index, 'htsCode', htsCode);
        await updateItem(index, 'htsDescription', htsDesc);

        // Also save confidence
        const asin = item.asin || item.sku;
        if (asin) {
          await apiService.save3PLASINData(asin, {
            hts_code: htsCode,
            hts_description: htsDesc,
            hts_confidence: topMatch.confidence || topMatch.accuracyMatch || 0
          });
        }
      } else {
        setError(`HTS classification failed for "${item.title.substring(0, 40)}..."`);
      }
    } catch (err: any) {
      setError(`HTS error: ${err.message}`);
    } finally {
      setProcessingItem(null);
    }
  };

  // Lookup manufacturer for a single item
  const lookupManufacturer = async (index: number) => {
    const item = items[index];
    const itemKey = `mfr-${index}`;
    setProcessingItem(itemKey);

    try {
      const result = await apiService.lookup3PLManufacturer(
        item.title,
        item.asin,
        item.countryOfOrigin
      );

      if (result.success && result.manufacturer) {
        const mfr = result.manufacturer;
        await updateItem(index, 'manufacturingAddress', mfr);
      } else {
        setError(`Manufacturer lookup failed for "${item.title.substring(0, 40)}..."`);
      }
    } catch (err: any) {
      setError(`Manufacturer error: ${err.message}`);
    } finally {
      setProcessingItem(null);
    }
  };

  // Bulk classify all HTS
  const bulkClassifyHTS = async () => {
    const itemsToClassify = items
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => !item.htsCode);

    if (itemsToClassify.length === 0) {
      setError('All items already have HTS codes');
      return;
    }

    setClassifyingAll(true);
    setBulkProgress({ current: 0, total: itemsToClassify.length, label: 'Classifying HTS codes' });

    for (let i = 0; i < itemsToClassify.length; i++) {
      setBulkProgress({ current: i + 1, total: itemsToClassify.length, label: 'Classifying HTS codes' });
      await classifyHTS(itemsToClassify[i].idx);
      // Small delay between calls
      if (i < itemsToClassify.length - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }

    setClassifyingAll(false);
    setBulkProgress({ current: 0, total: 0, label: '' });
  };

  // Bulk lookup all manufacturers
  const bulkLookupManufacturers = async () => {
    const itemsToLookup = items
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => !item.manufacturingAddress);

    if (itemsToLookup.length === 0) {
      setError('All items already have manufacturer addresses');
      return;
    }

    setLookingUpAll(true);
    setBulkProgress({ current: 0, total: itemsToLookup.length, label: 'Looking up manufacturers' });

    for (let i = 0; i < itemsToLookup.length; i++) {
      setBulkProgress({ current: i + 1, total: itemsToLookup.length, label: 'Looking up manufacturers' });
      await lookupManufacturer(itemsToLookup[i].idx);
      if (i < itemsToLookup.length - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }

    setLookingUpAll(false);
    setBulkProgress({ current: 0, total: 0, label: '' });
  };

  // Password prompt handler
  const handlePasswordAction = async () => {
    if (passwordAction === 'catalogue') {
      await uploadToCatalogue(password);
    } else if (passwordAction === 'ssb') {
      await submitToSSB(password);
    }
    setShowPasswordPrompt(false);
    setPassword('');
    setPasswordAction(null);
  };

  // Upload to catalogue
  const uploadToCatalogue = async (pw: string) => {
    setUploadingCatalogue(true);
    setError(null);

    try {
      const catalogueItems = items.map(item => ({
        asin: item.asin || item.sku,
        sku: item.sku,
        title: item.title,
        description: item.title,
        hts_code: item.htsCode || '',
        hts_description: item.htsDescription || '',
        countryOfOrigin: item.countryOfOrigin || '',
        country_of_origin: item.countryOfOrigin || '',
        declaredValueUSD: item.declaredValueUSD || item.pricePerUnit || 0,
        cogs_usd: item.declaredValueUSD || item.pricePerUnit || 0,
        manufacturing_address: item.manufacturingAddress,
        manufacturingAddress: item.manufacturingAddress,
        pricePerUnit: item.pricePerUnit
      }));

      const result = await apiService.upload3PLCatalogue(catalogueItems, pw);

      if (result.success) {
        const msg = `Catalogue upload: ${result.uploaded || 0} uploaded, ${result.existed || 0} already existed, ${result.failed || 0} failed`;
        alert(msg);
      } else {
        setError(result.error || 'Catalogue upload failed');
      }
    } catch (err: any) {
      setError(`Catalogue error: ${err.message}`);
    } finally {
      setUploadingCatalogue(false);
    }
  };

  // Submit to SSB
  const submitToSSB = async () => {
    if (!selectedOrder) return;

    // Validate all items have HTS codes
    const missing = items.filter(i => !i.htsCode);
    if (missing.length > 0) {
      setError(`${missing.length} item(s) still need HTS codes before submission`);
      return;
    }

    setSubmittingSSB(true);
    setError(null);

    try {
      const ssbItems = items.map(item => ({
        asin: item.asin || item.sku,
        sku: item.sku,
        title: item.title,
        description: item.title,
        quantity: item.quantity,
        pricePerUnit: item.declaredValueUSD || item.pricePerUnit || 0,
        declaredValueUSD: item.declaredValueUSD || item.pricePerUnit || 0,
        cogs_usd: item.declaredValueUSD || item.pricePerUnit || 0,
        hts_code: item.htsCode || '',
        htsCode: item.htsCode || '',
        hts_description: item.htsDescription || '',
        htsDescription: item.htsDescription || '',
        countryOfOrigin: item.countryOfOrigin || '',
        country_of_origin: item.countryOfOrigin || '',
        manufacturing_address: item.manufacturingAddress,
        manufacturingAddress: item.manufacturingAddress,
        isFood: item.isFood || false,
        isCosmeticProduct: item.isCosmeticProduct || false,
        fdaProductCode: item.fdaProductCode || '',
        weight: item.weight,
        weightUnit: item.weightUnit
      }));

      const result = await apiService.submit3PLToSSB(
        selectedOrder.id,
        selectedOrder.number,
        ssbItems,
        selectedOrder.deliverTo
      );

      if (result.success) {
        const uuid = result.uuid || result.data?.data?.uuid || 'N/A';
        alert(`Shipment submitted to SSB successfully! UUID: ${uuid}`);
        // Update local SSB submissions state immediately
        if (uuid !== 'N/A') {
          setSSBSubmissions(prev => ({
            ...prev,
            [selectedOrder.id]: {
              ssb_uuid: uuid,
              ssb_tracking_code: null,
              ssb_carrier: null,
              synced_to_veeqo: false,
              synced_at: null
            }
          }));
        }
      } else {
        setError(result.error || 'SSB submission failed');
      }
    } catch (err: any) {
      setError(`SSB error: ${err.message}`);
    } finally {
      setSubmittingSSB(false);
    }
  };

  // Sync SSB tracking to Veeqo
  const handleSyncToVeeqo = async (orderId: number) => {
    if (syncingOrderId) return;
    setSyncingOrderId(orderId);
    setError(null);

    try {
      // Pass extra data for auto-matched legacy orders
      const sub = ssbSubmissions[orderId];
      const order = orders.find(o => o.id === orderId);
      const result = await apiService.syncToVeeqo(
        orderId,
        order?.number,
        sub?.ssb_uuid,
        sub?.tracking_numbers?.[0] || sub?.ssb_tracking_code || undefined,
        sub?.ssb_carrier || undefined
      );
      if (result.success) {
        alert(`Tracking synced to Veeqo! ${result.data?.carrier || ''} / ${result.data?.tracking_code || ''}`);
        // Update local state
        setSSBSubmissions(prev => ({
          ...prev,
          [orderId]: {
            ...prev[orderId],
            ssb_tracking_code: result.data?.tracking_code || null,
            ssb_carrier: result.data?.carrier || null,
            synced_to_veeqo: true,
            synced_at: new Date().toISOString()
          }
        }));
      } else {
        setError(result.error || 'Sync failed');
      }
    } catch (err: any) {
      setError(`Sync error: ${err.message}`);
    } finally {
      setSyncingOrderId(null);
    }
  };

  // Link SSB UUID manually (for legacy orders)
  const handleLinkSSB = async () => {
    if (!selectedOrder) return;
    const uuid = prompt('Paste the SSB shipment UUID from the SSB portal:');
    if (!uuid || !uuid.trim()) return;

    try {
      setError(null);
      const result = await apiService.linkSSBUuid(selectedOrder.id, selectedOrder.number, uuid.trim());
      if (result.success) {
        setSSBSubmissions(prev => ({
          ...prev,
          [selectedOrder.id]: {
            ssb_uuid: uuid.trim(),
            ssb_tracking_code: null,
            ssb_carrier: null,
            synced_to_veeqo: false,
            synced_at: null
          }
        }));
        alert('SSB UUID linked successfully!');
      } else {
        setError(result.error || 'Failed to link SSB UUID');
      }
    } catch (err: any) {
      setError(`Link error: ${err.message}`);
    }
  };

  // SSB Rate Check for a single item
  const checkSSBRate = async (index: number) => {
    const item = items[index];
    const htsCode = item.htsCode || '';
    const countryOfOrigin = item.countryOfOrigin || '';
    const declaredValue = item.declaredValueUSD || 0;

    if (!htsCode || !countryOfOrigin || declaredValue <= 0) {
      setError('HTS Code, Country of Origin, and a valid Declared Value are required for SSB rate check');
      return;
    }

    if (htsCode.replace(/\D/g, '').length !== 10) {
      setError('A valid 10-digit HTS code is required for SSB rate check');
      return;
    }

    setSsbRateLoading(prev => ({ ...prev, [index]: true }));

    try {
      const response = await apiService.check3PLSSBRate({
        htsCode,
        countryOfOrigin,
        price: declaredValue,
        description: item.title,
        asin: item.asin || item.sku,
        manufacturingAddress: item.manufacturingAddress?.street_address
      });

      if (response.success && response.data) {
        setSsbVerifiedData(prev => ({ ...prev, [index]: response.data }));
      } else {
        setError('SSB rate check failed: ' + (response.error || 'Unknown error'));
      }
    } catch (err: any) {
      setError('SSB rate check error: ' + err.message);
    } finally {
      setSsbRateLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  // Bulk SSB Rate Check for all items
  const bulkCheckSSBRates = async () => {
    const itemsToCheck = items
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => item.htsCode && item.countryOfOrigin && (item.declaredValueUSD || 0) > 0 && item.htsCode.replace(/\D/g, '').length === 10);

    if (itemsToCheck.length === 0) {
      setError('No items are ready for SSB rate check (need HTS code, COO, and declared value)');
      return;
    }

    setBulkProgress({ current: 0, total: itemsToCheck.length, label: 'Checking SSB rates' });

    for (let i = 0; i < itemsToCheck.length; i++) {
      setBulkProgress({ current: i + 1, total: itemsToCheck.length, label: 'Checking SSB rates' });
      await checkSSBRate(itemsToCheck[i].idx);
      if (i < itemsToCheck.length - 1) {
        await new Promise(r => setTimeout(r, 1000)); // Longer delay - SSB rate check creates/deletes shipments
      }
    }

    setBulkProgress({ current: 0, total: 0, label: '' });
  };

  // Filter orders by search
  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.number?.toLowerCase().includes(term) ||
      order.customer?.name?.toLowerCase().includes(term) ||
      order.channel?.toLowerCase().includes(term) ||
      order.deliverTo?.city?.toLowerCase().includes(term)
    );
  });

  // Check item readiness
  const isItemReady = (item: VeeqoLineItem) => {
    return !!(item.htsCode && item.countryOfOrigin && item.declaredValueUSD);
  };

  const allItemsReady = items.length > 0 && items.every(isItemReady);
  const isBusy = classifyingAll || lookingUpAll || uploadingCatalogue || submittingSSB || !!processingItem;

  // ===== RENDER =====

  // Password prompt modal
  const renderPasswordPrompt = () => {
    if (!showPasswordPrompt) return null;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}>
        <div className="card" style={{ minWidth: '350px', maxWidth: '400px' }}>
          <h3 style={{ marginTop: 0 }}>
            {passwordAction === 'catalogue' ? 'Upload to Catalogue' : 'Submit to SSB'}
          </h3>
          <p style={{ color: '#666', fontSize: '14px' }}>Enter the password to proceed:</p>
          <input
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePasswordAction()}
            placeholder="Password"
            autoFocus
            style={{ marginBottom: '15px' }}
          />
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              className="button secondary"
              onClick={() => { setShowPasswordPrompt(false); setPassword(''); setPasswordAction(null); }}
            >
              Cancel
            </button>
            <button
              className="button primary"
              onClick={handlePasswordAction}
              disabled={!password}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Bulk progress bar
  const renderProgressBar = () => {
    if (bulkProgress.total === 0) return null;
    const pct = Math.round((bulkProgress.current / bulkProgress.total) * 100);
    return (
      <div style={{ marginBottom: '15px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '13px', color: '#666' }}>
          <span>{bulkProgress.label}</span>
          <span>{bulkProgress.current}/{bulkProgress.total} ({pct}%)</span>
        </div>
        <div style={{ height: '6px', backgroundColor: '#e9ecef', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, backgroundColor: '#0066cc', borderRadius: '3px', transition: 'width 0.3s ease' }} />
        </div>
      </div>
    );
  };

  // ===== LIST VIEW =====
  if (view === 'list') {
    return (
      <div>
        <div className="header">
          <div className="container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h1 style={{ margin: 0, fontSize: '24px' }}>3PL Orders</h1>
                <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>Veeqo Orders</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <button onClick={() => navigate('/staff')} className="button secondary" style={{ fontSize: '14px' }}>
                  Staff Portal
                </button>
                <button onClick={loadOrders} className="button primary" style={{ fontSize: '14px' }} disabled={loadingOrders}>
                  {loadingOrders ? 'Loading...' : 'Refresh'}
                </button>
                <button onClick={onLogout} className="button secondary">Logout</button>
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          {error && (
            <div className="error" style={{ marginBottom: '20px' }}>
              {error}
              <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>x</button>
            </div>
          )}

          {/* Filters */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              style={{
                padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px',
                fontSize: '14px', backgroundColor: '#fff', minWidth: '220px'
              }}
            >
              <option value="awaiting_fulfillment">Awaiting Fulfillment</option>
              <option value="shipped">Shipped</option>
              <option value="awaiting_payment">Awaiting Payment</option>
              <option value="on_hold">On Hold</option>
              <option value="partially_shipped">Partially Shipped</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <input
              type="text"
              className="input"
              placeholder="Search orders by number, customer, channel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: '400px', flex: 1 }}
            />
          </div>

          {loadingOrders ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '18px', color: '#666' }}>Loading Veeqo orders...</div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '18px', color: '#666' }}>
                {searchTerm ? 'No orders match your search' : 'No orders awaiting fulfillment'}
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {filteredOrders.map((order) => (
                  <div
                    key={order.id}
                    className="card"
                    style={{ padding: 0, overflow: 'hidden' }}
                  >
                    {/* Order header row */}
                    <div
                      onClick={() => openOrder(order)}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '12px 16px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef',
                        cursor: 'pointer', transition: 'background-color 0.15s ease'
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#e8f0fe')}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: '700', color: '#0066cc', fontSize: '15px', whiteSpace: 'nowrap' }}>
                          {order.number}
                        </span>
                        <span style={{
                          backgroundColor: order.status === 'shipped' ? '#d4edda' : order.status === 'cancelled' ? '#f8d7da' : '#e8f4fd',
                          color: order.status === 'shipped' ? '#155724' : order.status === 'cancelled' ? '#721c24' : '#0066cc',
                          padding: '2px 8px', borderRadius: '4px', fontSize: '11px', whiteSpace: 'nowrap', fontWeight: '600'
                        }}>
                          {order.status === 'awaiting_fulfillment' ? order.channel : `${order.status.replace(/_/g, ' ').toUpperCase()} - ${order.channel}`}
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>
                          {order.customer.name}
                        </span>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          {order.deliverTo.city}{order.deliverTo.state ? `, ${order.deliverTo.state}` : ''} {order.deliverTo.zip} {order.deliverTo.country}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
                        <span style={{ fontWeight: '600', fontSize: '14px' }}>
                          ${order.totalValue.toFixed(2)} {order.currency}
                        </span>
                        <span style={{ fontSize: '12px', color: '#888' }}>
                          {new Date(order.createdAt).toLocaleDateString()}
                        </span>
                        {/* SSB badges */}
                        {ssbSubmissions[order.id] && (
                          ssbSubmissions[order.id].synced_to_veeqo ? (
                            <span style={{
                              backgroundColor: '#d4edda', color: '#155724',
                              padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap'
                            }}>
                              SYNCED
                            </span>
                          ) : (
                            <span style={{
                              backgroundColor: '#fff3cd', color: '#856404',
                              padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap'
                            }}>
                              SSB: {ssbSubmissions[order.id].status_name || 'Submitted'}
                            </span>
                          )
                        )}
                        <span style={{
                          backgroundColor: order.status === 'awaiting_fulfillment' ? '#0066cc' : '#6c757d',
                          color: '#fff', padding: '4px 12px',
                          borderRadius: '4px', fontSize: '12px', fontWeight: '600'
                        }}>
                          {order.status === 'awaiting_fulfillment' ? 'Open' : 'View'}
                        </span>
                      </div>
                    </div>

                    {/* SSB Sync to Veeqo bar */}
                    {ssbSubmissions[order.id] && !ssbSubmissions[order.id].synced_to_veeqo && (
                      <div style={{
                        padding: '8px 16px', backgroundColor: '#fff8e1', borderBottom: '1px solid #e9ecef',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'
                      }}>
                        <span style={{ fontSize: '12px', color: '#856404' }}>
                          {ssbSubmissions[order.id].status_name && <strong>{ssbSubmissions[order.id].status_name}</strong>}
                          {ssbSubmissions[order.id].ssb_carrier && (
                            <> &middot; {ssbSubmissions[order.id].ssb_carrier}</>
                          )}
                          {(ssbSubmissions[order.id].tracking_numbers?.length || ssbSubmissions[order.id].ssb_tracking_code) && (
                            <> &middot; <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                              {ssbSubmissions[order.id].tracking_numbers?.join(', ') || ssbSubmissions[order.id].ssb_tracking_code}
                            </span></>
                          )}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSyncToVeeqo(order.id); }}
                          disabled={syncingOrderId === order.id}
                          style={{
                            backgroundColor: '#0066cc', color: '#fff', border: 'none', padding: '5px 14px',
                            borderRadius: '4px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                            opacity: syncingOrderId === order.id ? 0.6 : 1, whiteSpace: 'nowrap'
                          }}
                        >
                          {syncingOrderId === order.id ? 'Syncing...' : 'Sync to Veeqo'}
                        </button>
                      </div>
                    )}

                    {/* Synced tracking info */}
                    {ssbSubmissions[order.id]?.synced_to_veeqo && ssbSubmissions[order.id]?.ssb_tracking_code && (
                      <div style={{
                        padding: '6px 16px', backgroundColor: '#f0fdf4', borderBottom: '1px solid #e9ecef',
                        display: 'flex', gap: '12px', alignItems: 'center', fontSize: '12px'
                      }}>
                        <span style={{ fontWeight: '600', color: '#155724' }}>
                          {ssbSubmissions[order.id].ssb_carrier || 'Carrier'}
                        </span>
                        <span style={{ fontFamily: 'monospace', color: '#333', fontSize: '11px' }}>
                          {ssbSubmissions[order.id].ssb_tracking_code}
                        </span>
                        <span style={{ color: '#155724', fontSize: '11px' }}>
                          (Synced to Veeqo)
                        </span>
                      </div>
                    )}

                    {/* Tracking info for shipped orders */}
                    {order.shipments && order.shipments.length > 0 && (
                      <div style={{
                        padding: '6px 16px', backgroundColor: '#f0fdf4', borderBottom: '1px solid #e9ecef',
                        display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center'
                      }}>
                        {order.shipments.map((s, sIdx) => (
                          <div key={sIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                            <span style={{ fontWeight: '600', color: '#155724' }}>
                              {s.carrier || 'Carrier N/A'}
                            </span>
                            <span style={{ fontFamily: 'monospace', color: '#333', fontSize: '11px' }}>
                              {s.trackingUrl ? (
                                <a
                                  href={s.trackingUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ color: '#0066cc', textDecoration: 'underline' }}
                                >
                                  {s.trackingNumber}
                                </a>
                              ) : (
                                s.trackingNumber
                              )}
                            </span>
                            {s.shippedAt && (
                              <span style={{ color: '#888', fontSize: '11px' }}>
                                {new Date(s.shippedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Inline items list */}
                    <div style={{ padding: '0' }}>
                      {order.lineItems.map((li, liIdx) => (
                        <div
                          key={li.id || liIdx}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '8px 16px',
                            borderBottom: liIdx < order.lineItems.length - 1 ? '1px solid #f0f0f0' : 'none',
                            fontSize: '13px'
                          }}
                        >
                          {/* Thumbnail */}
                          {li.imageUrl ? (
                            <img
                              src={li.imageUrl}
                              alt=""
                              style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #eee', flexShrink: 0 }}
                            />
                          ) : (
                            <div style={{
                              width: '36px', height: '36px', backgroundColor: '#f5f5f5', borderRadius: '4px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '9px', color: '#bbb', flexShrink: 0
                            }}>
                              N/A
                            </div>
                          )}

                          {/* Qty badge */}
                          <span style={{
                            backgroundColor: '#e9ecef', borderRadius: '4px', padding: '2px 8px',
                            fontWeight: '700', fontSize: '13px', flexShrink: 0, minWidth: '32px', textAlign: 'center'
                          }}>
                            {li.quantity}x
                          </span>

                          {/* Title (truncated) */}
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#333' }}>
                            {li.title || li.variantTitle || 'Untitled'}
                          </span>

                          {/* ASIN / SKU */}
                          <span style={{
                            fontFamily: 'monospace', fontSize: '11px', color: '#666',
                            flexShrink: 0, whiteSpace: 'nowrap'
                          }}>
                            {li.asin && li.asin !== li.sku ? (
                              <span style={{ color: '#0066cc', fontWeight: '600' }}>{li.asin}</span>
                            ) : (
                              <span style={{ color: '#999' }}>{li.sku}</span>
                            )}
                          </span>

                          {/* Price */}
                          <span style={{ fontWeight: '600', flexShrink: 0, whiteSpace: 'nowrap', minWidth: '70px', textAlign: 'right' }}>
                            ${li.pricePerUnit.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                  <button
                    className="button secondary"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                    style={{ fontSize: '13px' }}
                  >
                    Previous
                  </button>
                  <span style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#666' }}>
                    Page {page} of {pagination.totalPages}
                  </span>
                  <button
                    className="button secondary"
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage(p => p + 1)}
                    style={{ fontSize: '13px' }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ===== DETAIL VIEW =====
  return (
    <div>
      <div className="header">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '24px' }}>Order #{selectedOrder?.number}</h1>
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                {selectedOrder?.channel} - {selectedOrder?.customer.name}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <button onClick={backToList} className="button secondary">
                &#8592; Back to Orders
              </button>
              <button onClick={onLogout} className="button secondary">Logout</button>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {error && (
          <div className="error" style={{ marginBottom: '20px' }}>
            {error}
            <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>x</button>
          </div>
        )}

        {/* Order Info Card */}
        {selectedOrder && (
          <div className="card" style={{ marginBottom: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>Customer</h3>
                <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.6' }}>
                  <div><strong>{selectedOrder.deliverTo.name}</strong></div>
                  {selectedOrder.deliverTo.businessName && <div>{selectedOrder.deliverTo.businessName}</div>}
                  <div>{selectedOrder.deliverTo.address1}</div>
                  {selectedOrder.deliverTo.address2 && <div>{selectedOrder.deliverTo.address2}</div>}
                  <div>
                    {selectedOrder.deliverTo.city}, {selectedOrder.deliverTo.state} {selectedOrder.deliverTo.zip}
                  </div>
                  <div>{selectedOrder.deliverTo.country}</div>
                  {selectedOrder.deliverTo.phone && <div>Phone: {selectedOrder.deliverTo.phone}</div>}
                </div>
              </div>
              <div>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>Order Details</h3>
                <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.6' }}>
                  <div>Order: <strong>#{selectedOrder.number}</strong></div>
                  <div>Channel: {selectedOrder.channel}</div>
                  <div>Items: {items.length}</div>
                  <div>Total: ${selectedOrder.totalValue.toFixed(2)} {selectedOrder.currency}</div>
                  <div>Date: {new Date(selectedOrder.createdAt).toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SSB Status Banner */}
        {selectedOrder && ssbSubmissions[selectedOrder.id] && (
          <div className="card" style={{
            marginBottom: '20px',
            backgroundColor: ssbSubmissions[selectedOrder.id].synced_to_veeqo ? '#f0fdf4' : '#fff8e1',
            border: ssbSubmissions[selectedOrder.id].synced_to_veeqo ? '1px solid #bbf7d0' : '1px solid #fde68a'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                  <span style={{
                    backgroundColor: ssbSubmissions[selectedOrder.id].synced_to_veeqo ? '#d4edda' : '#fff3cd',
                    color: ssbSubmissions[selectedOrder.id].synced_to_veeqo ? '#155724' : '#856404',
                    padding: '3px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '700'
                  }}>
                    {ssbSubmissions[selectedOrder.id].synced_to_veeqo ? 'SYNCED TO VEEQO' : `SSB: ${ssbSubmissions[selectedOrder.id].status_name || 'Submitted'}`}
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: '#555' }}>
                  SSB UUID: <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{ssbSubmissions[selectedOrder.id].ssb_uuid}</span>
                  {(ssbSubmissions[selectedOrder.id].tracking_numbers?.length || ssbSubmissions[selectedOrder.id].ssb_tracking_code) && (
                    <span style={{ marginLeft: '12px' }}>
                      Tracking: <strong>{ssbSubmissions[selectedOrder.id].ssb_carrier}</strong> / <span style={{ fontFamily: 'monospace' }}>{ssbSubmissions[selectedOrder.id].tracking_numbers?.join(', ') || ssbSubmissions[selectedOrder.id].ssb_tracking_code}</span>
                    </span>
                  )}
                </div>
              </div>
              {!ssbSubmissions[selectedOrder.id].synced_to_veeqo && (
                <button
                  onClick={() => handleSyncToVeeqo(selectedOrder.id)}
                  disabled={syncingOrderId === selectedOrder.id}
                  className="button primary"
                  style={{
                    fontSize: '13px', backgroundColor: '#0066cc', borderColor: '#0066cc',
                    opacity: syncingOrderId === selectedOrder.id ? 0.6 : 1
                  }}
                >
                  {syncingOrderId === selectedOrder.id ? 'Syncing...' : 'Sync to Veeqo'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                className="button secondary"
                onClick={bulkClassifyHTS}
                disabled={isBusy}
                style={{ fontSize: '13px' }}
              >
                {classifyingAll ? 'Classifying...' : 'Classify All HTS'}
              </button>
              <button
                className="button secondary"
                onClick={bulkLookupManufacturers}
                disabled={isBusy}
                style={{ fontSize: '13px' }}
              >
                {lookingUpAll ? 'Looking up...' : 'Lookup All Manufacturers'}
              </button>
              <button
                className="button secondary"
                onClick={bulkCheckSSBRates}
                disabled={isBusy || bulkProgress.label === 'Checking SSB rates'}
                style={{ fontSize: '13px', backgroundColor: '#e8f0fe', color: '#1565c0', borderColor: '#1565c0' }}
              >
                {bulkProgress.label === 'Checking SSB rates' ? 'Checking...' : 'Check All SSB Rates'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                className="button primary"
                onClick={() => { setPasswordAction('catalogue'); setShowPasswordPrompt(true); }}
                disabled={isBusy}
                style={{ fontSize: '13px' }}
              >
                {uploadingCatalogue ? 'Uploading...' : 'Upload to Catalogue'}
              </button>
              <button
                className="button primary"
                onClick={() => {
                  if (!allItemsReady) {
                    setError('All items must have HTS codes, country of origin, and declared values before submission');
                    return;
                  }
                  submitToSSB();
                }}
                disabled={isBusy || !allItemsReady}
                style={{
                  fontSize: '13px',
                  backgroundColor: allItemsReady ? '#28a745' : '#6c757d',
                  borderColor: allItemsReady ? '#28a745' : '#6c757d'
                }}
              >
                {submittingSSB ? 'Submitting...' : 'Submit to SSB'}
              </button>
            </div>
          </div>
          {renderProgressBar()}
          {/* Readiness indicator */}
          <div style={{ marginTop: '10px', fontSize: '13px', color: allItemsReady ? '#28a745' : '#dc3545' }}>
            {allItemsReady
              ? `All ${items.length} item(s) ready for submission`
              : `${items.filter(i => !isItemReady(i)).length} of ${items.length} item(s) still need data`
            }
          </div>
        </div>

        {/* Items Table */}
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', width: '50px' }}></th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', width: '120px' }}>SKU / ASIN</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '600' }}>Product</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', width: '50px' }}>Qty</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', width: '100px' }}>COO</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', width: '180px' }}>HTS Code</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', width: '200px' }}>Manufacturer</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', width: '120px' }}>Declared Value</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', width: '100px' }}>Duty (SSB)</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', width: '60px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const isProcessingHTS = processingItem === `hts-${index}`;
                const isProcessingMfr = processingItem === `mfr-${index}`;
                const ready = isItemReady(item);

                return (
                  <tr key={item.id || index} style={{ borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                    {/* Image */}
                    <td style={{ padding: '10px 12px' }}>
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt=""
                          style={{ width: '40px', height: '40px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #eee' }}
                        />
                      ) : (
                        <div style={{ width: '40px', height: '40px', backgroundColor: '#f0f0f0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#999' }}>
                          N/A
                        </div>
                      )}
                    </td>

                    {/* SKU / ASIN */}
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>SKU: {item.sku}</div>
                      <input
                        type="text"
                        value={item.asin}
                        onChange={(e) => updateItemASINText(index, e.target.value)}
                        onBlur={() => reloadASINData(index)}
                        style={{
                          width: '100%', padding: '4px 6px', border: '1px solid #ddd',
                          borderRadius: '3px', fontSize: '12px', fontFamily: 'monospace'
                        }}
                        placeholder="ASIN"
                        title="ASIN (editable - blur to reload saved data)"
                      />
                    </td>

                    {/* Product Title */}
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '500', lineHeight: '1.3' }}>
                        {item.title.length > 80 ? item.title.substring(0, 80) + '...' : item.title}
                      </div>
                      {item.variantTitle && item.variantTitle !== item.title && (
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{item.variantTitle}</div>
                      )}
                      {/* Food / Cosmetic badges */}
                      <div style={{ display: 'flex', gap: '6px', marginTop: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: item.isFood ? '#0d6efd' : '#999', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!item.isFood}
                            onChange={(e) => updateItem(index, 'isFood', e.target.checked)}
                            style={{ width: '12px', height: '12px' }}
                          />
                          Food
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: item.isCosmeticProduct ? '#0d6efd' : '#999', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!item.isCosmeticProduct}
                            onChange={(e) => updateItem(index, 'isCosmeticProduct', e.target.checked)}
                            style={{ width: '12px', height: '12px' }}
                          />
                          Cosmetic
                        </label>
                        {(item.isFood || item.isCosmeticProduct) && (
                          <input
                            type="text"
                            value={item.fdaProductCode || ''}
                            onChange={(e) => updateItem(index, 'fdaProductCode', e.target.value)}
                            placeholder="FDA Code"
                            style={{
                              width: '80px', padding: '1px 4px', border: '1px solid #ddd',
                              borderRadius: '3px', fontSize: '10px', fontFamily: 'monospace'
                            }}
                          />
                        )}
                      </div>
                    </td>

                    {/* Quantity */}
                    <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                      {item.quantity}
                    </td>

                    {/* Country of Origin */}
                    <td style={{ padding: '10px 12px' }}>
                      <select
                        value={item.countryOfOrigin || ''}
                        onChange={(e) => updateItem(index, 'countryOfOrigin', e.target.value)}
                        style={{
                          width: '100%', padding: '4px 6px', border: '1px solid #ddd',
                          borderRadius: '3px', fontSize: '12px', backgroundColor: item.countryOfOrigin ? '#fff' : '#fff3cd'
                        }}
                      >
                        <option value="">Select...</option>
                        {COUNTRY_OPTIONS.map(c => (
                          <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                        ))}
                      </select>
                    </td>

                    {/* HTS Code */}
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={item.htsCode || ''}
                          onChange={(e) => updateItem(index, 'htsCode', e.target.value)}
                          style={{
                            flex: 1, padding: '4px 6px', border: '1px solid #ddd',
                            borderRadius: '3px', fontSize: '12px', fontFamily: 'monospace',
                            backgroundColor: item.htsCode ? '#fff' : '#fff3cd'
                          }}
                          placeholder="0000.00.0000"
                        />
                        <button
                          onClick={() => classifyHTS(index)}
                          disabled={isBusy}
                          style={{
                            padding: '4px 8px', border: '1px solid #0066cc',
                            borderRadius: '3px', backgroundColor: isProcessingHTS ? '#ccc' : '#e8f4fd',
                            color: '#0066cc', fontSize: '11px', cursor: isBusy ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {isProcessingHTS ? '...' : 'AI'}
                        </button>
                      </div>
                      {item.htsDescription && (
                        <div style={{ fontSize: '10px', color: '#888', marginTop: '3px', lineHeight: '1.2' }}>
                          {item.htsDescription.substring(0, 60)}...
                        </div>
                      )}
                    </td>

                    {/* Manufacturer */}
                    <td style={{ padding: '10px 12px' }}>
                      {item.manufacturingAddress ? (
                        <div style={{ fontSize: '11px', lineHeight: '1.3' }}>
                          <div style={{ fontWeight: '600', color: '#333' }}>{item.manufacturingAddress.name}</div>
                          {item.manufacturingAddress.street_address && (
                            <div style={{ color: '#666' }}>{item.manufacturingAddress.street_address}</div>
                          )}
                          <div style={{ color: '#666' }}>
                            {[item.manufacturingAddress.city, item.manufacturingAddress.state].filter(Boolean).join(', ')}
                            {item.manufacturingAddress.zip ? ` ${item.manufacturingAddress.zip}` : ''}
                          </div>
                          <div style={{ color: '#555', fontWeight: '500' }}>{item.manufacturingAddress.country}</div>
                          {item.manufacturingAddress.confidence && (
                            <div style={{ color: '#888', fontSize: '9px', marginTop: '2px' }}>
                              Confidence: {item.manufacturingAddress.confidence}/5
                            </div>
                          )}
                          <button
                            onClick={() => lookupManufacturer(index)}
                            disabled={isBusy}
                            style={{
                              marginTop: '4px', padding: '2px 6px', border: '1px solid #ccc',
                              borderRadius: '3px', backgroundColor: '#f8f9fa',
                              color: '#666', fontSize: '9px', cursor: isBusy ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {isProcessingMfr ? '...' : 'Re-lookup'}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => lookupManufacturer(index)}
                          disabled={isBusy}
                          style={{
                            padding: '4px 8px', border: '1px solid #0066cc',
                            borderRadius: '3px', backgroundColor: isProcessingMfr ? '#ccc' : '#e8f4fd',
                            color: '#0066cc', fontSize: '11px', cursor: isBusy ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {isProcessingMfr ? 'Looking up...' : 'Lookup Mfr'}
                        </button>
                      )}
                    </td>

                    {/* Declared Value */}
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.declaredValueUSD || ''}
                          onChange={(e) => updateItem(index, 'declaredValueUSD', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '80px', padding: '4px 6px', border: '1px solid #ddd',
                            borderRadius: '3px', fontSize: '12px', textAlign: 'right',
                            backgroundColor: item.declaredValueUSD ? '#fff' : '#fff3cd'
                          }}
                        />
                        <span style={{ fontSize: '11px', color: '#888' }}>USD</span>
                      </div>
                    </td>

                    {/* Duty (SSB Check) */}
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {ssbVerifiedData[index] ? (
                        <div>
                          <div style={{ fontWeight: 700, color: '#1565c0', fontSize: '11px' }}>
                            ${ssbVerifiedData[index].dutyAmount.toFixed(2)}
                          </div>
                          <div style={{ color: '#666', fontSize: '9px' }}>{ssbVerifiedData[index].dutyRate}</div>
                          <div style={{ color: '#15803d', fontSize: '8px', fontWeight: 600 }}>SSB Verified</div>
                        </div>
                      ) : (
                        <button
                          onClick={() => checkSSBRate(index)}
                          disabled={isBusy || ssbRateLoading[index] || !item.htsCode || !item.countryOfOrigin || !item.declaredValueUSD}
                          style={{
                            padding: '3px 8px', fontSize: '10px', fontWeight: 600,
                            backgroundColor: ssbRateLoading[index] ? '#6c757d' : '#0d6efd',
                            color: '#fff', border: 'none', borderRadius: '3px',
                            cursor: (isBusy || ssbRateLoading[index] || !item.htsCode || !item.countryOfOrigin || !item.declaredValueUSD) ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {ssbRateLoading[index] ? '...' : 'SSB Check'}
                        </button>
                      )}
                    </td>

                    {/* Status */}
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{
                        width: '24px', height: '24px', borderRadius: '50%', margin: '0 auto',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: ready ? '#d4edda' : '#fff3cd',
                        color: ready ? '#155724' : '#856404',
                        fontSize: '14px'
                      }}>
                        {ready ? '\u2713' : '!'}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {renderPasswordPrompt()}
      </div>
    </div>
  );
};

export default ThreePLOrders;
